import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { MutateUpdateLapOffset } from "../../../utils/graphql/hooks/raceDataHooks";

export default function LapControls({ carColour }: { carColour: string }) {
  const updateLapOffset = MutateUpdateLapOffset();

  const raceConfig = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig,
    isEqual,
  );

  const strategyImport = useSelector(
    (state: RootState) => state.imports?.imports?.strategy?.data,
    isEqual,
  );

  const interventionsData = useSelector(
    (state: RootState) => state.interventions,
    isEqual,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const carHighlightColor = useMemo(() => {
    const driverNumber = jagDrivers?.[carColour];
    if (driverNumber && driverInfo?.[driverNumber]?.driverHighlightColourAlt) {
      return driverInfo[driverNumber].driverHighlightColourAlt;
    }
    return "#ff8c00";
  }, [jagDrivers, driverInfo, carColour]);

  const numberOfLaps =
    strategyImport?.NLapsRace != null && raceConfig?.lapOffset != null
      ? strategyImport.NLapsRace + raceConfig.lapOffset
      : "?";

  const lapsRemaining =
    numberOfLaps !== "?" && raceConfig?.currentLap != null
      ? numberOfLaps - raceConfig.currentLap + 1
      : "?";

  const currentLap = raceConfig?.currentLap ?? 0;

  const missMatch =
    raceConfig?.lapOffset != null && interventionsData?.addedLaps != null
      ? raceConfig.lapOffset !== interventionsData.addedLaps
      : false;

  return (
    <div className={styles.container}>
      <div
        className={`${styles.lapCountContainer} ${
          missMatch ? styles.mismatch : ""
        }`}
        style={{ "--mismatch-color": carHighlightColor } as React.CSSProperties}
      >
        <span>
          <span className={styles.full}>
            Lap {currentLap} / {numberOfLaps}
          </span>
          <span className={styles.compact}>
            {currentLap}/{numberOfLaps}
          </span>
        </span>
        <span>
          <span className={styles.full}>{lapsRemaining} Remaining</span>
          <span className={styles.compact}>{lapsRemaining} Rem</span>
        </span>
      </div>
      <div className={styles.lapOffsetContainer}>
        <span>Lap Offset:</span>
        <span>{raceConfig?.lapOffset ?? 0}</span>
        <div className={styles.lapOffsetButtons}>
          <button
            className={styles.lapOffsetButton}
            onClick={() => {
              updateLapOffset({
                variables: { carColour, increase: false },
              });
            }}
          >
            <FaMinus />
          </button>
          <button
            className={styles.lapOffsetButton}
            onClick={() => {
              updateLapOffset({
                variables: { carColour, increase: true },
              });
            }}
          >
            <FaPlus />
          </button>
        </div>
      </div>
    </div>
  );
}
